import * as React from "react";
import Link from "next/link";
import {
  Award,
  Building2,
  Eye,
  MailPlus,
  Receipt,
  Search,
  Users,
} from "lucide-react";
import {
  Button,
} from "@/components/ui/button";

/**
 * One icon, one heading, one line, one action. When the reader can't perform
 * the action (role or plan), pass a link instead of a button so we never show
 * a control that will fail.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        "flex flex-col items-center gap-3 px-6 py-12 text-center " + (className ?? "")
      }
    >
      <div className="flex size-12 items-center justify-center rounded-xl bg-accent">
        <Icon className="size-5 text-accent-foreground" />
      </div>
      <p className="text-base font-semibold">{title}</p>
      <p className="max-w-[42ch] text-[13px] leading-relaxed text-muted-foreground text-pretty">
        {description}
      </p>
      {action ? <div className="pt-0.5">{action}</div> : null}
    </div>
  );
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className="text-[13px] font-medium text-primary hover:underline">
      {children}
    </Link>
  );
}

export function NoCertificates({ canIssue, onIssue }: { canIssue: boolean; onIssue?: () => void }) {
  return (
    <EmptyState
      icon={Award}
      title="No certificates yet"
      description="Issue your first credential and the recipient gets a public verification page instantly."
      action={
        canIssue ? (
          <Button onClick={onIssue}>Issue certificate</Button>
        ) : (
          <TextLink href="/verify">Verify a certificate instead</TextLink>
        )
      }
    />
  );
}

export function NoSearchResults({
  query,
  onClear,
}: {
  query: string;
  onClear?: () => void;
}) {
  return (
    <EmptyState
      icon={Search}
      title="No certificates match"
      description={`Nothing matched “${query}”. Check the spelling, or widen the status filter.`}
      action={
        <button
          type="button"
          onClick={onClear}
          className="text-[13px] font-medium text-primary hover:underline"
        >
          Clear filters
        </button>
      }
    />
  );
}

export function NoTeamMembers({ canInvite }: { canInvite: boolean }) {
  return (
    <EmptyState
      icon={Users}
      title="You’re the only member"
      description="Invite colleagues so they can issue and manage certificates alongside you."
      action={
        canInvite ? (
          <Button asChild>
            <Link href="/invitations">Invite a member</Link>
          </Button>
        ) : (
          <TextLink href="/tenant/users">See who administers this tenant</TextLink>
        )
      }
    />
  );
}

export function NoInvitations({ canInvite }: { canInvite: boolean }) {
  return (
    <EmptyState
      icon={MailPlus}
      title="No pending invitations"
      description="Everyone you’ve invited has joined. New invites appear here until they’re accepted."
      action={canInvite ? <Button asChild>
          <Link href="/tenant/users">Invite a member</Link>
        </Button> : undefined}
    />
  );
}

export function NoInvoices({ periodEnds }: { periodEnds: string }) {
  return (
    <EmptyState
      icon={Receipt}
      title="No invoices yet"
      description={`Your first invoice arrives after this billing period closes on ${periodEnds}.`}
      action={<TextLink href="/tenant/subscription">View current usage</TextLink>}
    />
  );
}

export function NoTenants() {
  return (
    <EmptyState
      icon={Building2}
      title="No tenants yet"
      description="Create the first organisation to start issuing certificates on the platform."
      action={
        <Button asChild>
          <Link href="/admin/tenants?tab=create">Create tenant</Link>
        </Button>
      }
    />
  );
}

export function NeverViewed({ onCopyLink }: { onCopyLink?: () => void }) {
  return (
    <EmptyState
      icon={Eye}
      title="Not viewed yet"
      description="Nobody has opened this certificate’s verification page. Share the link or QR code to get started."
      action={
        <button
          type="button"
          onClick={onCopyLink}
          className="text-[13px] font-medium text-primary hover:underline"
        >
          Copy verification link
        </button>
      }
    />
  );
}
